/** Export modal — frame a printable A4 map and generate a PDF.
 *
 * Renders a second, non-interactive MapLibre instance (via <Map preview>)
 * initialised to the live map's current view, sized to the exact A4 aspect
 * (portrait/landscape) of the chosen page. The user pans/zooms to frame the
 * extent; overlay chips (title, legend, scale, north arrow) sit on top of the
 * map exactly where they'll print — the modal is a true WYSIWYG preview.
 *
 * The legend is viewport-scoped: measures / no-work / species colours only
 * appear if their geometry actually falls inside the framed extent. Species
 * selected in the filters are always listed by name; they gain a colour swatch
 * only when that species colour is visible in-frame.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Map as WatercourseMap } from "./Map";
import { useLanguageContext } from "../hooks/useLanguage";
import { getMonthLabels } from "../constants/months";
import { ZOOM_BREAKPOINTS } from "../constants/mapZoom";
import { boundsToPolygonRing, lineIntersectsPolygon } from "../utils/geometry";
import { formatSelectedMonths } from "../utils/exportLegend";
import type { ExportLegend, LegendLayer, LegendSpecies } from "../utils/exportLegend";
import { LAYER_LABEL_KEYS } from "./LayerControls";
import { computeScaleBar, exportMapToPdf, getPageSize, OVERLAY } from "../utils/exportPdf";
import type { ExportOrientation } from "../utils/exportPdf";
import type {
  FilterState,
  GeoJSONFeatureCollection,
  LayerVisibility,
  MapMode,
  RegionConfig,
} from "../types";

type Basemap = "street" | "satellite";

interface Bounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

/** Structural type for the bits of the MapLibre instance we read for export. */
type PreviewMapInstance = {
  getCanvas: () => HTMLCanvasElement;
  getBounds: () => {
    getWest: () => number;
    getSouth: () => number;
    getEast: () => number;
    getNorth: () => number;
  };
};

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  /** effectiveConfig — carries any user-picked measure colours. */
  config: RegionConfig;
  /** Mode the modal opens in; can then be switched inside the modal. */
  initialMode: MapMode;
  municipality: string;
  filters: FilterState;
  focusData: GeoJSONFeatureCollection | null;
  speciesData: GeoJSONFeatureCollection | null;
  measuresData: GeoJSONFeatureCollection | null;
  noWorkData: GeoJSONFeatureCollection | null;
  layersData: Record<string, GeoJSONFeatureCollection>;
  visibility: LayerVisibility;
  initialCenter: [number, number] | null;
  initialZoom: number | null;
}

const noop = () => {};

export function ExportModal({
  open,
  onClose,
  config,
  initialMode,
  municipality,
  filters,
  focusData,
  speciesData,
  measuresData,
  noWorkData,
  layersData,
  visibility,
  initialCenter,
  initialZoom,
}: ExportModalProps) {
  const { t, lang } = useLanguageContext();
  const [mode, setMode] = useState<MapMode>(initialMode);
  const [orientation, setOrientation] = useState<ExportOrientation>("landscape");
  const [basemap, setBasemap] = useState<Basemap>("street");
  const [mapOpacity, setMapOpacity] = useState(1);
  const [bounds, setBounds] = useState<Bounds | null>(null);
  const [zoom, setZoom] = useState<number>(initialZoom ?? 10);
  const [exporting, setExporting] = useState(false);
  const [boxSize, setBoxSize] = useState<{ w: number; h: number } | null>(null);
  const mapAreaRef = useRef<HTMLDivElement>(null);
  const mapWrapRef = useRef<HTMLDivElement>(null);

  const speciesName = useCallback(
    (id: string, fallback: string): string => {
      const key = `species_${id}`;
      const val = t(key);
      return val === key ? fallback : val;
    },
    [t],
  );

  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setBounds(null);
      setOrientation("landscape");
      setBasemap("street");
      setMapOpacity(1);
      setMode(initialMode);
      setZoom(initialZoom ?? 10);
    }
  }, [open, initialMode, initialZoom]);

  const page = useMemo(() => getPageSize(orientation), [orientation]);
  const aspect = page.w / page.h;

  // Fit the A4 aspect box inside the available area (deterministic px sizing so
  // MapLibre gets a real size and the crop/overlays are exact).
  useEffect(() => {
    if (!open) return;
    const el = mapAreaRef.current;
    if (!el) return;
    const recompute = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width === 0 || height === 0) return;
      const w = width / height > aspect ? height * aspect : width;
      const h = width / height > aspect ? height : width / aspect;
      setBoxSize({ w, h });
    };
    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, aspect]);

  const getPreviewMap = useCallback((): PreviewMapInstance | null => {
    const el = mapWrapRef.current?.querySelector(".maplibregl-map") as
      | (HTMLElement & { __maplibreMap?: PreviewMapInstance })
      | null;
    return el?.__maplibreMap ?? null;
  }, []);

  // Orientation change resizes the map without firing a move event; re-read
  // bounds once it settles so the legend stays in sync.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      const map = getPreviewMap();
      if (!map) return;
      const b = map.getBounds();
      setBounds({ west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() });
    }, 350);
    return () => window.clearTimeout(id);
  }, [open, orientation, boxSize, getPreviewMap]);

  const monthLabels = useMemo(() => getMonthLabels(lang), [lang]);

  const legend = useMemo<ExportLegend>(() => {
    const ring = bounds ? boundsToPolygonRing(bounds) : null;
    const monthsLabel = formatSelectedMonths(filters.months, monthLabels, t("all_year"));

    // Which selected species have habitat inside the frame? And is there a
    // "both species" overlap stretch (drawn green) in-frame?
    const habitatSpeciesInFrame = new Set<string>();
    let overlapInFrame = false;
    if (ring) {
      for (const feature of speciesData?.features ?? []) {
        const id = feature.properties.species_id;
        if (id === "both") {
          if (!overlapInFrame && lineIntersectsPolygon(feature.geometry, ring)) {
            overlapInFrame = true;
          }
          continue;
        }
        if (typeof id === "string" && filters.species.has(id) && !habitatSpeciesInFrame.has(id)) {
          if (lineIntersectsPolygon(feature.geometry, ring)) habitatSpeciesInFrame.add(id);
        }
      }
    }

    // In planning mode, species-coloured overview lines are drawn from
    // measuresData, so species swatches need to follow that source rather than
    // the full habitat layer.
    const planningSpeciesInFrame = new Set<string>();
    const presentMeasures = new Set<string>();
    if (ring && mode === "planning") {
      for (const feature of measuresData?.features ?? []) {
        if (!lineIntersectsPolygon(feature.geometry, ring)) continue;
        const id = feature.properties.species_id;
        if (typeof id === "string" && filters.species.has(id)) planningSpeciesInFrame.add(id);
        const measures = Array.isArray(feature.properties.measures)
          ? (feature.properties.measures as string[])
          : [];
        measures.forEach((mm) => presentMeasures.add(mm));
      }
    }

    // A species shows its colour only when that colour is actually on the map:
    //  - habitat mode: species lines are coloured when the species layer is on.
    //  - planning mode: species colours only appear in the zoomed-out overview
    //    tier (zoom < overviewMax). Once zoomed in, lines are measure-coloured,
    //    so the species drop to name-only. And only if habitat is in-frame.
    const speciesLayerActive = visibility.species !== false;
    const measuresLayerActive = visibility.measures !== false;
    const speciesTierActive = zoom < ZOOM_BREAKPOINTS.overviewMax;
    const species: LegendSpecies[] = config.species
      .filter((s) => filters.species.has(s.id))
      .map((s) => ({
        label: speciesName(s.id, s.name),
        color: s.color,
        showColor:
          (mode === "habitats" ? habitatSpeciesInFrame.has(s.id) : planningSpeciesInFrame.has(s.id)) &&
          (mode === "habitats"
            ? speciesLayerActive
            : measuresLayerActive && speciesTierActive),
      }));

    // "Both species" (green) overlap — only drawn on the map in habitat mode
    // with the species layer on. Rendered as one more species-style entry.
    if (mode === "habitats" && speciesLayerActive && overlapInFrame) {
      species.push({ label: t("both_species"), color: config.overlap_colors.both, showColor: true });
    }

    // Visible base layers (protected areas, waterways, …) get a legend entry
    // whenever their toggle is on, in either mode.
    const layers: LegendLayer[] = Object.entries(config.base_layers)
      .filter(([name]) => visibility[name] !== false)
      .map(([name, cfg]) => ({
        label: t(LAYER_LABEL_KEYS[name] ?? name),
        color: cfg.color,
        opacity: cfg.opacity,
        fill: name !== "waterways",
      }));

    if (mode === "habitats" || !ring || !measuresLayerActive) {
      return { measures: [], hasNoWork: false, species, layers, monthsLabel, showTimePeriod: false };
    }

    // Planning mode: measures present in-frame + no-work presence.
    const measures = config.measures
      .filter((mm) => presentMeasures.has(mm.id))
      .map((mm) => ({ label: mm.label, color: mm.color }));

    const hasNoWork = (noWorkData?.features ?? []).some((feature) =>
      lineIntersectsPolygon(feature.geometry, ring),
    );

    return { measures, hasNoWork, species, layers, monthsLabel, showTimePeriod: true };
  }, [bounds, config.base_layers, config.measures, config.overlap_colors.both, config.species, filters.months, filters.species, measuresData, mode, monthLabels, noWorkData, speciesData, speciesName, t, visibility, zoom]);

  const handleExport = useCallback(async () => {
    const map = getPreviewMap();
    if (!map) return;
    setExporting(true);
    try {
      const mapImage = map.getCanvas().toDataURL("image/png");
      const b = map.getBounds();
      const modeLabel = t(mode === "habitats" ? "habitat_mode" : "planning_mode");
      const safeName = (municipality || "export").replace(/[^a-zA-Z0-9]/g, "_");
      const date = new Date().toISOString().slice(0, 10);
      await exportMapToPdf({
        mapImage,
        bounds: { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() },
        orientation,
        legend,
        municipality,
        modeLabel,
        logoUrl: config.branding.logo ? `/${config.branding.logo.replace(/^\/+/, "")}` : null,
        labels: {
          speciesHeading: t(mode === "habitats" ? "export_species_habitat" : "export_species_considered"),
          timePeriod: t("export_time_period"),
          noWork: t("no_work_permitted"),
          disclaimer: `${t("data_stamp_label")}: ${config.branding.data_stamp}. ${t("data_disclaimer")}`,
        },
        fileName: `flima-export-${safeName}-${mode}-${date}.pdf`,
      });
    } catch (err) {
      console.error("Map export failed:", err);
    } finally {
      setExporting(false);
    }
  }, [config.branding.data_stamp, config.branding.logo, getPreviewMap, legend, mode, municipality, orientation, t]);

  if (!open) return null;

  // mm → px scale for the WYSIWYG overlays.
  const mmPx = boxSize ? boxSize.w / page.w : 0;
  const scaleBar = bounds ? computeScaleBar(bounds, page.w) : null;
  const speciesHeading = t(mode === "habitats" ? "export_species_habitat" : "export_species_considered");

  const segButton = <T,>(value: T, current: T, onClick: (v: T) => void, label: string) => (
    <button
      type="button"
      onClick={() => onClick(value)}
      aria-pressed={value === current}
      className={`min-h-10 rounded px-2 py-1 font-utility text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none sm:px-3 ${
        value === current
          ? "bg-white text-flima-spruce shadow-sm"
          : "text-flima-slate/60 hover:text-flima-spruce"
      }`}
    >
      {label}
    </button>
  );

  const content = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("export_pdf")}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-0 sm:p-4"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t("close")}
        className="absolute inset-0 bg-flima-spruce/40 backdrop-blur-sm"
        tabIndex={-1}
      />

      <div className="flima-screen relative flex w-screen max-w-[1600px] flex-col overflow-hidden border border-flima-spruce/10 bg-white shadow-panel sm:h-[95vh] sm:w-[97vw] sm:rounded-2xl">
        {/* Header */}
        <div className="flex shrink-0 flex-col gap-3 border-b border-flima-spruce/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="min-w-0 pr-12 sm:pr-0">
            <h2 className="font-display text-lg font-semibold text-flima-spruce">
              {t("export_pdf")}
            </h2>
            <p className="truncate font-utility text-[0.62rem] uppercase tracking-[0.14em] text-flima-slate/55">
              {municipality}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center">
            <div className="grid grid-cols-2 rounded-md border border-flima-spruce/15 bg-flima-mist/40 p-0.5 sm:flex">
              {segButton<MapMode>("habitats", mode, setMode, t("habitat_mode"))}
              {segButton<MapMode>("planning", mode, setMode, t("planning_mode"))}
            </div>
            <div className="grid grid-cols-2 rounded-md border border-flima-spruce/15 bg-flima-mist/40 p-0.5 sm:flex">
              {segButton<ExportOrientation>("landscape", orientation, setOrientation, t("export_landscape"))}
              {segButton<ExportOrientation>("portrait", orientation, setOrientation, t("export_portrait"))}
            </div>
            <div className="grid grid-cols-2 rounded-md border border-flima-spruce/15 bg-flima-mist/40 p-0.5 sm:flex">
              {segButton<Basemap>("street", basemap, setBasemap, t("basemap_street"))}
              {segButton<Basemap>("satellite", basemap, setBasemap, t("basemap_satellite"))}
            </div>
            <label className="flex min-h-10 items-center gap-2 rounded-md border border-flima-spruce/15 bg-flima-mist/40 px-2 font-utility text-xs font-medium text-flima-slate/70">
              <span className="whitespace-nowrap">{t("map_opacity")}</span>
              <input
                type="range"
                min="0.2"
                max="1"
                step="0.05"
                value={mapOpacity}
                onChange={(event) => setMapOpacity(Number(event.currentTarget.value))}
                aria-label={t("map_opacity")}
                className="h-1 w-20 accent-flima-spruce"
              />
              <span className="w-8 text-right tabular-nums text-flima-slate/60">
                {Math.round(mapOpacity * 100)}%
              </span>
            </label>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-flima-spruce px-4 text-sm font-semibold text-white shadow-float transition-colors hover:bg-flima-river focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {exporting ? t("export_generating") : t("export_download")}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-2xl leading-none text-flima-slate/55 transition-colors hover:bg-flima-mist hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none sm:static"
              aria-label={t("close")}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>

        {/* WYSIWYG page preview */}
        <div className="flex min-h-0 flex-1 items-center justify-center bg-flima-mist/50 p-2 sm:p-4">
          <div ref={mapAreaRef} className="flex h-full w-full items-center justify-center">
            <div
              ref={mapWrapRef}
              className="relative overflow-hidden rounded-sm shadow-lg ring-1 ring-flima-spruce/20"
              style={boxSize ? { width: boxSize.w, height: boxSize.h } : { width: 1, height: 1 }}
            >
            <WatercourseMap
              config={config}
              focusData={focusData}
              speciesData={speciesData}
              measuresData={measuresData}
              noWorkData={noWorkData}
              layersData={layersData}
              mode={mode}
              visibility={visibility}
              selectedSpeciesCount={filters.species.size}
              highlightMeasureId={null}
              lineOverlayMeasureId={null}
              highlightNoWork={false}
              lineOverlayNoWork={false}
              selectionMode="none"
              polygonPoints={[]}
              polygonClosed={false}
              viewportPolygon={null}
              onPolygonPointAdd={noop}
              onPolygonFinish={noop}
              onPolygonUndo={noop}
              onSelectionClear={noop}
              onViewportCapture={noop}
              onZoomChange={setZoom}
              onMeasureClick={noop}
              preview
              initialCenter={initialCenter}
              initialZoom={initialZoom}
              onBoundsChange={setBounds}
              basemap={basemap}
              mapOpacity={mapOpacity}
            />

            {/* Overlays — positioned in mm-scaled px so they match the PDF. */}
            {boxSize ? (
              <>
                {/* Title chip (top-left) */}
                <div
                  className="pointer-events-none absolute flex items-center gap-2 rounded bg-white/95 shadow-sm ring-1 ring-flima-spruce/10"
                  style={{
                    left: OVERLAY.margin * mmPx,
                    top: OVERLAY.margin * mmPx,
                    padding: OVERLAY.pad * mmPx,
                    maxWidth: boxSize.w * 0.55,
                  }}
                >
                  {config.branding.logo ? (
                    <img
                      src={`/${config.branding.logo.replace(/^\/+/, "")}`}
                      alt=""
                      className="w-auto shrink-0 object-contain"
                      style={{ height: 11 * mmPx }}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : null}
                  <div className="min-w-0 leading-tight">
                    <div
                      className="truncate font-display font-semibold text-flima-spruce"
                      style={{ fontSize: OVERLAY.titleMm * mmPx }}
                    >
                      {municipality || "—"}
                    </div>
                    <div
                      className="truncate text-flima-slate/60"
                      style={{ fontSize: OVERLAY.subMm * mmPx }}
                    >
                      {t(mode === "habitats" ? "habitat_mode" : "planning_mode")}
                      {"  ·  "}
                      {new Date().toISOString().slice(0, 10)}
                    </div>
                  </div>
                </div>

                {/* North arrow (top-right) */}
                <div
                  className="pointer-events-none absolute flex flex-col items-center rounded bg-white/95 shadow-sm ring-1 ring-flima-spruce/10"
                  style={{
                    right: OVERLAY.margin * mmPx,
                    top: OVERLAY.margin * mmPx,
                    padding: 1.5 * mmPx,
                    width: 10 * mmPx,
                  }}
                >
                  <svg viewBox="0 0 20 24" style={{ width: 6 * mmPx }} fill="none" aria-hidden="true">
                    <path d="M10 2 L4 18 L10 14 L16 18 Z" fill="#173F35" />
                  </svg>
                  <span className="font-display font-bold text-flima-spruce" style={{ fontSize: 2.6 * mmPx }}>
                    N
                  </span>
                </div>

                {/* Scale bar (bottom-left) */}
                {scaleBar ? (
                  <div
                    className="pointer-events-none absolute flex items-end gap-1 rounded bg-white/95 px-1 shadow-sm ring-1 ring-flima-spruce/10"
                    style={{
                      left: OVERLAY.margin * mmPx,
                      bottom: OVERLAY.margin * mmPx,
                      padding: 1.2 * mmPx,
                    }}
                  >
                    <div className="flex flex-col items-stretch" style={{ width: scaleBar.mm * mmPx }}>
                      <span
                        className="text-flima-slate"
                        style={{ fontSize: 2.6 * mmPx, lineHeight: 1 }}
                      >
                        {scaleBar.label}
                      </span>
                      <span
                        className="border-b-2 border-l-2 border-r-2 border-flima-slate"
                        style={{ height: 1.5 * mmPx }}
                      />
                    </div>
                  </div>
                ) : null}

                {/* Legend box (bottom-right) */}
                <div
                  className="pointer-events-none absolute overflow-hidden rounded bg-white/95 shadow-sm ring-1 ring-flima-spruce/10"
                  style={{
                    right: OVERLAY.margin * mmPx,
                    bottom: (OVERLAY.margin + OVERLAY.legendLift) * mmPx,
                    width: OVERLAY.legendW * mmPx,
                    padding: OVERLAY.pad * mmPx,
                    fontSize: OVERLAY.entryMm * mmPx,
                  }}
                >
                  {legend.measures.map((sw) => (
                    <div key={sw.label} className="mb-0.5 flex items-center gap-1.5">
                      <span
                        className="shrink-0 rounded-full"
                        style={{ width: OVERLAY.swatchW * mmPx, height: 1.2 * mmPx, backgroundColor: sw.color }}
                      />
                      <span className="text-flima-slate" style={{ lineHeight: 1.15 }}>{sw.label}</span>
                    </div>
                  ))}
                  {legend.hasNoWork ? (
                    <div className="mb-0.5 flex items-center gap-1.5">
                      <span
                        className="shrink-0 border-t-[3px] border-dashed"
                        style={{ width: OVERLAY.swatchW * mmPx, borderColor: "#DC2626" }}
                      />
                      <span className="text-flima-slate" style={{ lineHeight: 1.15 }}>
                        {t("no_work_permitted")}
                      </span>
                    </div>
                  ) : null}

                  {legend.species.length > 0 ? (
                    <div style={{ marginTop: legend.measures.length || legend.hasNoWork ? OVERLAY.gap * mmPx : 0 }}>
                      <div
                        className="font-display font-semibold text-flima-spruce"
                        style={{ fontSize: OVERLAY.entryMm * mmPx }}
                      >
                        {speciesHeading}
                      </div>
                      {legend.species.map((s) => (
                        <div key={s.label} className="mt-0.5 flex items-center gap-1.5">
                          {s.showColor ? (
                            <span
                              className="shrink-0 rounded-full"
                              style={{ width: OVERLAY.swatchW * mmPx, height: 1.2 * mmPx, backgroundColor: s.color }}
                            />
                          ) : null}
                          <span
                            className={s.showColor ? "text-flima-slate" : "text-flima-slate/55"}
                            style={{ lineHeight: 1.15 }}
                          >
                            {s.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {legend.layers.length > 0 ? (
                    <div style={{ marginTop: OVERLAY.gap * mmPx }}>
                      {legend.layers.map((ly) => (
                        <div key={ly.label} className="mt-0.5 flex items-center gap-1.5">
                          {ly.fill ? (
                            <span
                              className="shrink-0 rounded-sm border"
                              style={{
                                width: 3 * mmPx,
                                height: 3 * mmPx,
                                backgroundColor: ly.color,
                                borderColor: ly.color,
                                opacity: Math.max(ly.opacity, 0.45),
                              }}
                            />
                          ) : (
                            <span
                              className="shrink-0 rounded-full"
                              style={{ width: OVERLAY.swatchW * mmPx, height: 1.2 * mmPx, backgroundColor: ly.color }}
                            />
                          )}
                          <span className="text-flima-slate" style={{ lineHeight: 1.15 }}>{ly.label}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {legend.showTimePeriod ? (
                    <div style={{ marginTop: OVERLAY.gap * mmPx }}>
                      <div
                        className="font-display font-semibold text-flima-spruce"
                        style={{ fontSize: OVERLAY.entryMm * mmPx }}
                      >
                        {t("export_time_period")}
                      </div>
                      <div className="text-flima-slate" style={{ lineHeight: 1.15 }}>{legend.monthsLabel}</div>
                    </div>
                  ) : null}

                  <div
                    className="text-flima-slate/55"
                    style={{ marginTop: OVERLAY.gap * mmPx, lineHeight: 1.15 }}
                  >
                    {t("data_stamp_label")}: {config.branding.data_stamp}. {t("data_disclaimer")}
                  </div>
                </div>
              </>
            ) : null}
            </div>
          </div>
        </div>

        {/* Footer hint — emphasised: this is how the user frames the printed
            extent, and it was easy to miss when styled as fine print. */}
        <div className="shrink-0 border-t border-flima-spruce/10 px-4 py-2.5 sm:px-5">
          <p className="flex items-center gap-2 rounded-lg border border-flima-river/30 bg-flima-river/10 px-3 py-2 text-sm font-semibold text-flima-spruce">
            <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-flima-river" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8h.01M11 12h1v4h1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t("export_hint")}
          </p>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
